import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { classNames, Spacing } from '@vkontakte/vkui';
import { xray } from 'lib/xray';
import throttle from 'lodash.throttle';
import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { sendStartEventTimer } from 'reactApp/appHelpers/featuresHelpers';
import { multiTariffMobile } from 'reactApp/appHelpers/featuresHelpers/features/multiTariffMobile';
import {
    quotaLandingLoadProductsThresholdMobile,
    quotaLandingLoadProductsTimeout,
} from 'reactApp/appHelpers/featuresHelpers/features/quotaLandingLoadProductsTimeouts';
import { START_EVENT_TIMER_MS } from 'reactApp/constants';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import {
    changeMultiTariffMobileActiveTabAction,
    loadQuotaLandingProducts,
    showLandingTariffsAction,
} from 'reactApp/modules/quotaLanding/quotaLanding.module';
import { getMultiTariffMobileActiveTab } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { useIntersecObsrWithParams } from 'reactApp/sections/FamilyLanding/hooks/useIntersecObsrWithParams';
import { Container } from 'reactApp/sections/QuotaLanding/Container/Container';
import { BuyButtons } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/BuyButtons';
import { Features } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/Features/Features';
import { MultiTariffCardLoader } from 'reactApp/sections/QuotaLanding/MultiTariffsListMobile/MultiTariffCardLoader/MultiTariffCardLoader';
import { ETabs } from 'reactApp/sections/QuotaLanding/QuotaLanding.data';
import { sendQuotaBlockViewDWH, sendQuotaGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { useMobileBuyClick } from 'reactApp/sections/QuotaLanding/TariffsList/useMobileBuyClick';
import { useBuyClick } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffPlansCard/useBuyClick';
import { getMultiTariffMobileProductsSpacesListByTab } from 'reactApp/sections/QuotaLanding/TariffsPlans/TariffsPlans.selectors';
import type { RootState } from 'reactApp/store';
import { TabMenu } from 'reactApp/ui/Menu/TabMenu';
import { ETabMenuMod } from 'reactApp/ui/Menu/TabMenu.types';
import { TabMenuItem } from 'reactApp/ui/Menu/TabMenuItem';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { sendTmrGoal, TMR3473039 } from 'reactApp/utils/tmr';
import type { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './MultiTariffsListMobile.css';

interface MultiTariffsListMobileProps {
    changeTab?: (tab) => void;
    isIOSPromo?: boolean;
    isAndroidPromo?: boolean;
    isAnonymous: boolean;
    headerHeight?: number;
    isTariffsPage: boolean;
    isMobilePayAvailable: boolean;
    isMobilePayFromGetParam: boolean;
    mayPromo2025Enabled?: boolean;
}

const tabOptions = [
    ...(multiTariffMobile === 'c'
        ? [
              {
                  value: ETabsName.basic,
                  text: 'Минимальный',
              },
          ]
        : []),
    {
        value: ETabsName.advanced,
        text: 'Основной',
    },
    {
        value: ETabsName.family,
        text: 'Семейный',
    },
];

export const MultiTariffsListMobile = memo(
    forwardRef<HTMLDivElement | null, MultiTariffsListMobileProps>(
        (
            {
                headerHeight = 0,
                isTariffsPage,
                isMobilePayAvailable,
                isMobilePayFromGetParam,
                isIOSPromo,
                isAndroidPromo,
                mayPromo2025Enabled,
                changeTab,
            },
            ref
        ) => {
            const dispatch = useDispatch();

            const swiperRef = useRef<SwiperInstance | null>(null);
            const productsLoadTimer = useRef<NodeJS.Timeout | null>(null);

            const { isLoading, isLoaded } = useSelector(ProductsSelectors.getLifeCycleState);
            const activeTariffTab = useSelector(getMultiTariffMobileActiveTab);

            const isAlmostFullFreeUser = useSelector(UserSelectors.isAlmostFullFreeUser);

            const {
                spaces,
                products,
                selectorDataLoaded = false,
            } = useSelector((state: RootState) => getMultiTariffMobileProductsSpacesListByTab(state, Boolean(mayPromo2025Enabled)));
            const [currentSpace, setCurrentSpace] = useState<number>(0);

            const { monthProduct, yearProduct } = products?.[currentSpace] || {};

            const showSpecialFamilyCard = activeTariffTab === ETabsName.family && multiTariffMobile === 'b';

            const onBuyClick = useBuyClick({
                isMobilePayAvailable,
                isMobilePayFromGetParam,
                isIOSPromo,
                isAndroidPromo,
                enabledMidasPayment: true,
            });

            const onMobileBuyClick = useMobileBuyClick({
                mayPromo2025Enabled,
                isTariffsPage,
                onBuyClick,
            });

            // отправляем событие с периодом из фичи или раз в 10 минут
            const sendStartViewEvent = throttle(() => {
                dispatch(showLandingTariffsAction());
            }, sendStartEventTimer || START_EVENT_TIMER_MS);

            useEffect(() => {
                xray.send('quota_show_tariffs_list');

                if (Boolean(quotaLandingLoadProductsTimeout) && !isTariffsPage) {
                    xray.send(`quota_tariffs_timeout_init_mobile`);
                }

                return () => {
                    if (productsLoadTimer?.current) {
                        clearTimeout(productsLoadTimer.current);
                        productsLoadTimer.current = null;
                    }
                };
            }, []);

            useEffect(() => {
                // При переключении табов восстанавливаем позицию слайдера на первый элемент.
                if (swiperRef.current) {
                    swiperRef.current?.slideTo(0);
                }

                setCurrentSpace(spaces[0]);
            }, [activeTariffTab, spaces.length]);

            const onIntersecting = useCallback(() => {
                if (sendStartEventTimer) {
                    sendStartViewEvent();
                }

                emitAnalyticEvent(AnalyticEventNames.TARIFFS_SHOWN, { isAlmostFullFreeUser });
                sendQuotaGa({ action: 'tariff', label: 'view' });
                sendTmrGoal({ id: TMR3473039, goal: 'tariff-view' });
                sendQuotaBlockViewDWH('tariffs');

                changeTab?.(ETabs.tariffs);

                /**
                 * Загружаем продукты если юзера доскролил до блока с тарифами и остался
                 * на нем какое-то время (quotaLandingLoadProductsTimeout)
                 */
                if (!isLoaded && !isLoading && !productsLoadTimer?.current && !isTariffsPage && quotaLandingLoadProductsTimeout) {
                    const id = setTimeout(() => {
                        xray.send('quota_tariffs_timeout_load');
                        dispatch(loadQuotaLandingProducts());
                    }, quotaLandingLoadProductsTimeout);

                    productsLoadTimer.current = id;
                }
            }, [isLoaded, isLoading, dispatch, isTariffsPage, changeTab]);

            const onIntersectingOut = useCallback(() => {
                if (productsLoadTimer?.current) {
                    /**
                     * Сбрасываем таймер загрузки продуктов
                     * Нужно чтобы продукты не грузились когда происходит быстрый якорный скролл.
                     */
                    clearTimeout(productsLoadTimer.current);
                    productsLoadTimer.current = null;
                }
            }, []);

            const refIntersection = useIntersecObsrWithParams({
                onIntersecting,
                onIntersectingOut,
                isMobile: true,
                headerHeight,
                threshold: quotaLandingLoadProductsThresholdMobile,
            });

            const handleSwiper = useCallback((swiper: SwiperInstance) => {
                swiperRef.current = swiper;
            }, []);

            const handleSwiperDestroy = useCallback(() => {
                swiperRef.current = null;
            }, []);

            const tariffCard = useMemo(() => {
                if (!selectorDataLoaded || !currentSpace || !isLoaded) {
                    return <MultiTariffCardLoader />;
                }

                return (
                    <>
                        <div className={styles.card}>
                            {showSpecialFamilyCard ? (
                                <div className={styles.cardFamily}>
                                    <Spacing size={12} />
                                    <div className={styles.familyTariff}>
                                        <div className={styles.value}>{bytesToNDigits(currentSpace, 3).space}</div>
                                        <div className={styles.units}>{bytesToNDigits(currentSpace, 3).units}</div>
                                    </div>
                                    <Spacing size={16} />
                                </div>
                            ) : (
                                <div className={styles.list}>
                                    <Swiper spaceBetween={12} slidesPerView={2.2} onSwiper={handleSwiper} onDestroy={handleSwiperDestroy}>
                                        {spaces.map((space, index) => {
                                            return (
                                                <SwiperSlide key={`${space}-${activeTariffTab}`} virtualIndex={index}>
                                                    <div
                                                        className={classNames(styles.tariff, {
                                                            [styles.tariffActive]: space === currentSpace,
                                                        })}
                                                        onClick={() => {
                                                            emitAnalyticEvent(AnalyticEventNames.MULTITARIFF_SIZE_CLICK_M);
                                                            sendQuotaGa({
                                                                action: 'change-tariff',
                                                                name_button_from: activeTariffTab,
                                                                tariff: yearProduct?.id,
                                                            });
                                                            setCurrentSpace(space);
                                                        }}
                                                    >
                                                        <div className={styles.value}>{bytesToNDigits(space, 3).space}</div>
                                                        <div className={styles.units}>{bytesToNDigits(space, 3).units}</div>
                                                        {/* хз какое тут условие для шильдика 'Выгодно' мб что-то посложнее надо */}
                                                        {space === SPACE_LIST.tb_1 && <div className={styles.label}>Выгодно</div>}
                                                    </div>
                                                </SwiperSlide>
                                            );
                                        })}
                                    </Swiper>
                                </div>
                            )}
                            <Spacing size={24} />
                            <Features
                                space={currentSpace}
                                product={yearProduct}
                                onBuyClick={onMobileBuyClick}
                                activeTariffTab={activeTariffTab}
                            />
                        </div>
                        <Spacing size={16} />
                        {monthProduct && yearProduct && (
                            <BuyButtons monthProduct={monthProduct} yearProduct={yearProduct} onBuy={onMobileBuyClick} />
                        )}
                    </>
                );
            }, [monthProduct, yearProduct, spaces, currentSpace, selectorDataLoaded, isLoaded]);

            return (
                <div ref={refIntersection}>
                    <div ref={ref}>
                        <Container isNarrow>
                            <TabMenu mod={ETabMenuMod.multiTariffMobile}>
                                {tabOptions.map(({ value, text }) => (
                                    <TabMenuItem
                                        key={value}
                                        name={text}
                                        value={value}
                                        active={value === activeTariffTab}
                                        onClick={() => {
                                            sendQuotaGa({
                                                action: 'change-tariff-line',
                                                name_button_from: value,
                                            });
                                            dispatch(changeMultiTariffMobileActiveTabAction(value));
                                            emitAnalyticEvent(AnalyticEventNames.MULTITARIFF_TYPE_CLICK_M);
                                        }}
                                        changeHash={false}
                                    />
                                ))}
                            </TabMenu>
                            <Spacing size={20} />
                            {tariffCard}
                        </Container>
                    </div>
                </div>
            );
        }
    )
);

MultiTariffsListMobile.displayName = 'MultiTariffsListMobile';
